import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { TextField, Typography } from "@mui/material";
import { differenceInYears } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Controller,
  type DeepPartial,
  type FieldErrors,
  FormProvider,
  useForm,
} from "react-hook-form";
import { z } from "zod";
import Block from "@/components/Block";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { analytics } from "@/utils/analytics";
import { getGuestPersons, getTotalGuests, scrollToFirstError } from "@/utils/booking";
import BookingStepLayout from "./components/BookingStepLayout";
import GuestInfoForm from "./components/GuestInfoForm";
import PersonalInfoForm from "./components/PersonalInfoForm";
import VehicleDetailsForm from "./components/VehicleDetailsForm";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type BookingFormData = {
  name: string;
  lastname: string;
  emails: {
    mail: string;
    confirmMail: string;
  };
  phone?: string;
  street: string;
  street_additional?: string;
  zip: string;
  city: string;
  country: {
    code: string;
  };
  guests: Array<{
    person_id: number;
    first_name: string;
    last_name: string;
    birthdate: Date | null;
  }>;
  car: {
    license_plate?: string;
    length?: number;
    width?: number;
  };
  message?: string;
};

export default function Details() {
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const guestPersons = useMemo(
    () => getGuestPersons(state.selectedPersons, campgroundData.campground.persons),
    [state.selectedPersons, campgroundData.campground.persons],
  );

  const bookingInfoSchema = useMemo(() => {
    const personMap = new Map(guestPersons.map((p) => [p.id, p]));

    const guestSchema = z
      .object({
        person_id: z.number(),
        first_name: z.string().min(1, t`First name is required`),
        last_name: z.string().min(1, t`Last name is required`),
        birthdate: z.preprocess(
          (value) => (value === null ? undefined : value),
          z
            .date({ message: t`Birthdate is required` })
            .max(new Date(), t`Birthdate cannot be in the future`),
        ),
      })
      .superRefine((guest, ctx) => {
        if (!state.dateFrom || !guest.birthdate) return;
        const person = personMap.get(guest.person_id);
        if (!person) return;
        const ageAtCheckIn = differenceInYears(state.dateFrom, guest.birthdate);
        if (ageAtCheckIn < person.age_from || ageAtCheckIn > person.age_to) {
          ctx.addIssue({
            code: "custom",
            message: t`Age must be between ${person.age_from} and ${person.age_to} for ${person.name}`,
            path: ["birthdate"],
          });
        }
      });

    const emailsSchema = z
      .object({
        mail: z.string().min(1, t`Email is required`).regex(emailRegex, t`Invalid email address`),
        confirmMail: z
          .string()
          .min(1, t`Email confirmation is required`)
          .regex(emailRegex, t`Invalid email address`),
      })
      .refine((data) => data.mail === data.confirmMail, {
        message: t`Emails do not match`,
        path: ["confirmMail"],
      });

    return z.object({
      name: z.string().min(1, t`First Name is required`),
      lastname: z.string().min(1, t`Last Name is required`),
      emails: emailsSchema,
      phone: z.string().optional(),
      street: z.string().min(1, t`Address Line 1 is required`),
      street_additional: z.string().optional(),
      zip: z.string().min(1, t`Postal Code is required`),
      city: z.string().min(1, t`City is required`),
      country: z.object({
        code: z.string().min(1, t`Country is required`),
      }),
      guests: z.array(guestSchema),
      car: z.object({
        license_plate: z.string().optional(),
        length: z
          .preprocess(
            (val) => (val === "" ? undefined : val),
            z.coerce
              .number()
              .min(1, t`Length must be at least 1m`)
              .max(50, t`Length cannot be greater than 50m`)
              .optional(),
          )
          .optional(),
        width: z
          .preprocess(
            (val) => (val === "" ? undefined : val),
            z.coerce
              .number()
              .min(1, t`Width must be at least 1m`)
              .max(50, t`Width cannot be greater than 10m`)
              .optional(),
          )
          .optional(),
      }),
      message: z.string().optional(),
    });
  }, [state.dateFrom, guestPersons]);

  const billing = state.billing;

  type BookingFormData = z.input<typeof bookingInfoSchema>;

  const methods = useForm<BookingFormData>({
    resolver: zodResolver(bookingInfoSchema),
    mode: "onBlur",
    defaultValues: {
      name: billing.name,
      lastname: billing.lastname,
      emails: {
        mail: billing.mail,
        confirmMail: billing.mail,
      },
      phone: billing.phone,
      street: billing.street,
      street_additional: billing.street_additional || "",
      zip: billing.zip,
      city: billing.city,
      country: billing.country,
      guests: Array.from(
        {
          length: getTotalGuests(state.selectedPersons, campgroundData.campground.persons, "human"),
        },
        (_, index) => ({
          person_id: guestPersons[index]?.id ?? 0,
          first_name: state.travellers[index]?.first_name || "",
          last_name: state.travellers[index]?.last_name || "",
          birthdate: state.travellers[index]?.birthdate
            ? new Date(state.travellers[index].birthdate)
            : null,
        }),
      ),
      car: {
        license_plate: state.car.license_plate,
        length: state.car.length,
        width: state.car.width,
      },
      message: state.billing.message,
    },
  });

  const {
    formState: { errors },
    control,
    handleSubmit,
  } = methods;

  const saveFormDataToContext = useCallback(
    (data: DeepPartial<BookingFormData>) => {
      actions.setBilling("name", data.name);
      actions.setBilling("lastname", data.lastname);
      actions.setBilling("mail", data.emails?.mail ?? "");
      actions.setBilling("phone", data.phone);
      actions.setBilling("street", data.street);
      actions.setBilling("street_additional", data.street_additional ?? "");
      actions.setBilling("zip", data.zip);
      actions.setBilling("city", data.city);
      actions.setBilling("country", data.country);
      actions.setBilling("message", data.message);

      if (data.car) {
        actions.setCar("license_plate", data.car.license_plate);
        actions.setCar("length", data.car.length);
        actions.setCar("width", data.car.width);
      }

      data.guests?.forEach((guest, index) => {
        if (!guest) {
          return;
        }
        actions.setTraveller(index, "first_name", guest.first_name);
        actions.setTraveller(index, "last_name", guest.last_name);
        actions.setTraveller(index, "birthdate", guest.birthdate);
      });
    },
    [actions],
  );

  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const subscription = methods.watch((formValues) => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        saveFormDataToContext(formValues);
        actions.setDetailsValid(false);
      }, 1000);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [methods, saveFormDataToContext, actions.setDetailsValid]);

  const expectedGuestCount = getTotalGuests(
    state.selectedPersons,
    campgroundData.campground.persons,
    "human",
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
  useEffect(() => {
    const currentGuestCount = methods.getValues("guests").length;
    const diff = expectedGuestCount - currentGuestCount;

    if (diff !== 0) {
      const currentGuests = methods.getValues("guests");

      if (diff > 0) {
        // Guest count increased - add new slots
        const newSlots = Array.from({ length: diff }, (_, i) => ({
          person_id: guestPersons[currentGuestCount + i]?.id ?? 0,
          first_name: state.travellers[currentGuestCount + i]?.first_name ?? "",
          last_name: state.travellers[currentGuestCount + i]?.last_name ?? "",
          birthdate: state.travellers[currentGuestCount + i]?.birthdate ?? null,
        }));
        methods.setValue("guests", [...currentGuests, ...newSlots]);
      } else {
        // Guest count decreased - remove excess slots
        methods.setValue("guests", currentGuests.slice(0, expectedGuestCount));
      }
    }
  }, [expectedGuestCount, methods, guestPersons]);

  const onSubmit = (data: BookingFormData) => {
    // Flush pending auto-save to ensure latest data is captured
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      saveFormDataToContext(data);
    }

    actions.setDetailsValid(true);
    analytics.capture("details_submitted", {
      billing_country: data.country.code,
      vehicle_length: data.car.length,
      vehicle_width: data.car.width,
    });
  };

  const onError = (errors: FieldErrors) => {
    enqueueSnackbar(t`Please fix the highlighted fields before continuing`, {
      variant: "error",
    });
    scrollToFirstError(errors);
  };

  const submitAndReturnResult = async (): Promise<boolean> => {
    let isValid = false;

    await handleSubmit(
      async (data) => {
        onSubmit(data);
        isValid = true;
      },
      (errors) => {
        onError(errors);
        isValid = false;
      },
    )();

    return isValid;
  };

  return (
    <FormProvider {...methods}>
      <form noValidate>
        <BookingStepLayout
          nextAction={submitAndReturnResult}
          nextButtonText={t`Proceed to payment`}
        >
          <PersonalInfoForm />
          <GuestInfoForm />
          <VehicleDetailsForm />

          <Block flushOnMobile>
            <Typography sx={{ mb: 2 }} variant="h3">
              <Trans>Message to the campground</Trans>
            </Typography>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t`Additional notes for the campsite operator`}
                  multiline
                  rows={9}
                  variant="filled"
                  fullWidth
                  error={!!errors.message}
                  helperText={errors.message?.message as string | undefined}
                />
              )}
            />
          </Block>
        </BookingStepLayout>
      </form>
    </FormProvider>
  );
}
